import { PartTypeActionLiveSearchSerialiseType } from '../@types';
import { PartConditionOptions } from '../@types/part.type';
import { PartActionResponse, CustomisePartActionCard, PartActionType, PartActionWarningDependancies, PartActionValidateType, PartActionTypes, PartActionCardHelperText, CreateBulkPartActionsRequest } from '../@types/partAction.type';
import { ServiceTypeCode } from '../configs/enums';
import { LOCATION_CANNOT_EMPTY, NOTE_CANNOT_EMPTY, PART_SERIAL_CANNOT_EMPTY, PART_TYPE_CANNOT_EMPTY, QUANTITY_CANNOT_EMPTY, SAMPLE_PART_ACTION_CARD_HELPER_TEXT } from '../constants/partAction';
import { useSubLocationsForCreateServices } from '../queries/live-search-query';
import { isEmptyString } from './common';
import { getPartTypeActionLiveSearchSerialiseType } from './part-type';

export const arrayComparison = (obj:PartActionResponse, array:CustomisePartActionCard[]) => {
  return array.some((item: CustomisePartActionCard) => (item.value.actionId === obj.id) && item.value.type === obj.actionTypeCode);
};

export const filterActionCard = (card: CustomisePartActionCard, cardSet: CustomisePartActionCard[]) => {
  return cardSet.filter((partActionCardData) => partActionCardData.value.id !== card.value.id);
};

export const triggerWarning = (partAction: PartActionType, warningDependancies: PartActionWarningDependancies) => {
  switch (partAction) {
  case PartActionType.REPLACE: 
    return `Warning: In ${warningDependancies.in.serial1 ? `serial ${warningDependancies.in.serial1}` : ''} Part Type ${warningDependancies.in.partTypeName} is different to Out ${warningDependancies.out.serial1 ? `serial ${warningDependancies.out.serial1}` : ''} Part Type ${warningDependancies.out.partTypeName}.  A replace action is intended to be a like-for-like swap. Ensure the utility of the inbound part matches expectations.`;
  }
};

export const partActionConditionValidation = (cardType: string, condition: string, serial: string) => {
  switch (cardType) {
  case PartActionValidateType.INSTALL:
  case PartActionValidateType.REPLACEIN: 
    if (condition.toLowerCase() !== PartConditionOptions.GOOD && condition.toLowerCase() !== PartConditionOptions.RESERVED) {
      return `Warning: Part ${serial} is in Condition ${condition} and may not be suitable for usage.`;
    }
    break;
  case PartActionValidateType.REMOVE:
  case PartActionValidateType.DOAIN:
  case PartActionValidateType.REPLACEOUT: 
    if (condition.toLowerCase() !== PartConditionOptions.INSTALLED && condition.toLowerCase() !== PartConditionOptions.RESERVED) {
      return `Warning: Part ${serial} is in Condition ${condition} and may not be suitable for usage.`;
    }
    break;
  case PartActionValidateType.DOAOUT: 
    if (condition.toLowerCase() !== PartConditionOptions.RESERVED) {
      return `Warning: Part ${serial} is in Condition ${condition} and may not be suitable for usage.`;
    }
    break;
  default:
    return '';
  }
};

export const partActionInValidations = (condition: string, serial: string, nodeName: string) => {
  if (!condition) {
    return '';
  } else if (condition === PartConditionOptions.GOOD) {
    return `Warning: ${serial} is in an unexpected location; please confirm correct part has been selected prior to proceeding.`;
  } else {
    return `Warning: ${serial} is in an unexpected location ${nodeName}, condition ${condition}. You may proceed, however you are recommended to seek assistance.`;
  }
};

export const usePartActionOutValidations = async (validationType: string, serial: string, serviceParentNode: string, serviceBaseNode: string, partBaseNode: string, partBaseNodeId: number, partTypeName: string) => {
  const subLocationsForCreateServicesQuery = useSubLocationsForCreateServices();
  
  if (validationType === 'site-hierarchy') {
    const subLocationsResult = await subLocationsForCreateServicesQuery.mutateAsync(partBaseNodeId);
    const main = subLocationsResult?.find(subLocation => subLocation.isMain);

    const partParentNode = main ? `${main} /` : '';
    serviceBaseNode = serviceBaseNode ? `/ ${serviceBaseNode}` : '';
    
    return `Warning: ${serial} is not at ${serviceParentNode} ${serviceBaseNode}, but at ${partParentNode} ${partBaseNode}. This is an unexpected part location; please confirm the correct part has been selected prior to proceeding. installed in ${partTypeName}: ${serial}`;
  } else {
    const baseOrParentnode = serviceBaseNode ? serviceBaseNode : serviceParentNode;

    return `Warning: ${serial} is not at ${baseOrParentnode}, but at ${partBaseNode}.  Please confirm the correct part has been selected prior to proceeding.`;
  }
};

export const getPartActionTypesDropdownData = (partActionData: PartActionTypes[]) => {
  return partActionData.map(partAction => ({
    value: partAction.code,
    label: partAction.name
  }));
};

export const validatePartActionHelperTexts = (partActionHelperTexts: PartActionCardHelperText[], partActionCardDataset: CustomisePartActionCard[], serviceTypeCode: string) => {
  for (let i = 0; i < partActionCardDataset.length; i++) {
    const partActionCard = partActionCardDataset[i];
    let helperText: PartActionCardHelperText = partActionHelperTexts.find((text) => text.id === partActionCard.value?.id) || SAMPLE_PART_ACTION_CARD_HELPER_TEXT;
    helperText.id = partActionCard.value.id;
    
    if ([PartActionType.INSTALL, PartActionType.TEST, PartActionType.REPAIR, PartActionType.ADJUST].includes(partActionCard?.value?.type as PartActionType)) {
      helperText = {
        ...helperText,
        in: {
          ...helperText.in,
          partTypeHelperText: !partActionCard?.value.partTypeId ? PART_TYPE_CANNOT_EMPTY : '',
          partSerialHelperText: !partActionCard?.value.serial ? PART_SERIAL_CANNOT_EMPTY : '',
          ...(partActionCard?.value?.type === PartActionType.INSTALL && serviceTypeCode === ServiceTypeCode.Repair && { locationHelperText: !partActionCard?.value.ns_partId && !partActionCard?.value.location ? LOCATION_CANNOT_EMPTY : '' }),
          qtyHelperText: !partActionCard?.value.ns_partId && !partActionCard?.value.qty ? QUANTITY_CANNOT_EMPTY : ''
        }
      };
    } else if (partActionCard?.value?.type === PartActionType.REPLACE) {
      helperText = {
        ...helperText,
        in: {
          ...helperText.in,
          partTypeHelperText: !partActionCard?.inValue?.partTypeId ? PART_TYPE_CANNOT_EMPTY : '',
          partSerialHelperText: !partActionCard?.inValue?.serial ? PART_SERIAL_CANNOT_EMPTY : '',
        },
        out: {
          ...helperText.out,
          partTypeHelperText: !partActionCard?.outValue?.partTypeId ? PART_TYPE_CANNOT_EMPTY : '',
          partSerialHelperText: !partActionCard?.outValue?.serial ? PART_SERIAL_CANNOT_EMPTY : '',
          noteHelperText: !partActionCard?.outValue?.notes ? NOTE_CANNOT_EMPTY : ''
        }
      };
    } else if (partActionCard?.value?.type === PartActionType.REMOVE) {
      helperText = {
        ...helperText,
        out: {
          ...helperText.out,
          partTypeHelperText: !partActionCard?.outValue?.partTypeId ? PART_TYPE_CANNOT_EMPTY : '',
          partSerialHelperText: !partActionCard?.outValue?.serial ? PART_SERIAL_CANNOT_EMPTY : '',
          locationHelperText: !partActionCard?.outValue?.ns_partId && !partActionCard?.outValue?.location ? LOCATION_CANNOT_EMPTY : ''
        }
      };
    } else if (partActionCard?.value?.type === PartActionType.CLEAN) {
      helperText = {
        ...helperText,
        in: {
          ...helperText.in,
          partTypeHelperText: !partActionCard.value.partTypeId ? PART_TYPE_CANNOT_EMPTY : '',
          partSerialHelperText: !partActionCard.value.serial ? PART_SERIAL_CANNOT_EMPTY : ''
        },
      };
    } else if (partActionCard?.value?.type === PartActionType.DOA) {
      helperText = {
        ...helperText,
        out: {
          ...helperText.out,
          partTypeHelperText: !partActionCard?.outValue?.partTypeId ? PART_TYPE_CANNOT_EMPTY : '',
          partSerialHelperText: !partActionCard?.outValue?.serial ? PART_SERIAL_CANNOT_EMPTY : '',
          locationHelperText: !partActionCard?.outValue?.ns_partId && !partActionCard?.outValue?.location ? LOCATION_CANNOT_EMPTY : ''
        },
      };
    }

    partActionHelperTexts[i] = helperText;
  }
  return partActionHelperTexts;
};

export const getPendingPartIdsToCheck= (partActionCardDataset: CustomisePartActionCard[]) => {
  const pendingPartIdsToCheck: number[] = [];
  for (const partActionCard of partActionCardDataset) {
    if (partActionCard.value.partId && [PartActionType.INSTALL, PartActionType.TEST, PartActionType.REPAIR, PartActionType.ADJUST].includes(partActionCard?.value?.type as PartActionType)) {
      pendingPartIdsToCheck.push(partActionCard.value.partId);
    }
  }
  return pendingPartIdsToCheck;
};

export const preProcessPartActions = async(
  partActionCardDataset: CustomisePartActionCard[],
  createPendingPartForCard: (cardId: number) => Promise<CustomisePartActionCard | null>,
  serviceTypeCode: string
) => {
  for (let i = 0; i < partActionCardDataset.length; i++) {
    let partActionCard = partActionCardDataset[i];

    if ([PartActionType.INSTALL, PartActionType.TEST, PartActionType.REPAIR, PartActionType.ADJUST].includes(partActionCard?.value?.type as PartActionType)) {
      const isSuccess = !!partActionCard.value.partTypeId &&
      (!!partActionCard.value.serial || !!partActionCard.value.qty) &&
      (partActionCard?.value?.type === PartActionType.INSTALL && serviceTypeCode === ServiceTypeCode.Repair ? (!!partActionCard.value.ns_partId && !!partActionCard.value.location) : true);
      if (isSuccess) {
        // create pending part (if any) for this card before part action execution
        const updatedCard = await createPendingPartForCard(partActionCard.value.id);
        partActionCard = updatedCard ? { value: { ...updatedCard.value, isExceptionalCreation: true }, outValue: updatedCard.outValue, inValue: updatedCard.inValue, execute: true } : { value: partActionCard.value, outValue: partActionCard.outValue, inValue: partActionCard.inValue, execute: true };
      }
    } else if (partActionCard?.value?.type === PartActionType.REPLACE) {
      const isInSuccess = !!partActionCard?.inValue?.partTypeId && (!!partActionCard?.inValue?.serial || getPartTypeActionLiveSearchSerialiseType(partActionCard?.inValue?.category) === PartTypeActionLiveSearchSerialiseType.NonSerialised);
      const isOutSuccess = !!partActionCard?.outValue?.partTypeId && (!!partActionCard?.outValue?.serial || getPartTypeActionLiveSearchSerialiseType(partActionCard?.outValue?.category) === PartTypeActionLiveSearchSerialiseType.NonSerialised) && !!partActionCard?.outValue?.notes;
      if (isInSuccess && isOutSuccess) {
        // create pending parts (if any) for this card before part action execution
        const updatedCard = await createPendingPartForCard(partActionCard.value.id);
        partActionCard = updatedCard ? { value: { ...updatedCard.value, isExceptionalCreation: true }, outValue: updatedCard.outValue, inValue: updatedCard.inValue, execute: true } : { value: partActionCard.value, outValue: partActionCard.outValue, inValue: partActionCard.inValue, execute: true };
      } else {
        partActionCard = { value: partActionCard.value, outValue: partActionCard.outValue, inValue: partActionCard.inValue, execute: false };
      }
    } else if (partActionCard?.value?.type === PartActionType.REMOVE) {
      // create pending part (if any) for this card before part action execution
      const updatedCard = await createPendingPartForCard(partActionCard.value.id);
      const isSuccess = !!partActionCard?.outValue?.partTypeId && (!!partActionCard?.outValue?.serial || (!!partActionCard?.outValue?.ns_partId && !!partActionCard?.outValue?.location));
      if (isSuccess) {
        partActionCard = updatedCard ? { value: { ...updatedCard.value, isExceptionalCreation: true }, outValue: updatedCard.outValue, inValue: updatedCard.inValue, execute: true } : { value: partActionCard.value, outValue: partActionCard.outValue, inValue: partActionCard.inValue, execute: true };
      } else {
        partActionCard = { value: partActionCard.value, outValue: partActionCard.outValue, inValue: partActionCard.inValue, execute: false };
      }
    } else if (partActionCard?.value?.type === PartActionType.CLEAN) {
      const isSuccess = !!partActionCard.value.partTypeId || !!partActionCard.value.serial;
      partActionCard = { value: partActionCard.value, execute: isSuccess };
    } else if (partActionCard?.value?.type === PartActionType.DOA) {
      const isSuccess = !!partActionCard?.outValue?.partTypeId && (!!partActionCard?.outValue?.serial || (!!partActionCard?.outValue?.ns_partId && !!partActionCard?.outValue?.location));
      partActionCard = { value: partActionCard.value, outValue: partActionCard.outValue, inValue: partActionCard.inValue, execute: isSuccess };
    }

    partActionCardDataset[i] = partActionCard;
  }
  return partActionCardDataset;
};

export const mapPartActions = (partActionCardDataset: CustomisePartActionCard[], serviceId: number) => {
  const mappedPartactions: CreateBulkPartActionsRequest[] = [];
  for (const partAction of partActionCardDataset) {
    let inValue: any = {};
    let outValue: any = {};

    if (partAction?.value?.type === PartActionType.INSTALL) {
      inValue = {
        id: partAction.value?.id,
        partTypeId: partAction.value.partTypeId,
        name: partAction.value?.name,
        sw: partAction.value?.sw,
        hw: partAction.value?.hw,
        fw: partAction.value?.fw,
        ft: partAction.value?.ft,
        serial: partAction.value?.serial,
        qty: partAction.value?.qty,
        category: partAction.value?.category,
        condition: partAction.value?.condition,
        location: partAction.value?.location,
        notes: partAction.value?.notes,
        type: partAction.value?.type,
        images: [],
        partId: partAction.value?.partId,
        ns_partId: partAction.value?.ns_partId,
        ...(partAction.value?.desiredParentSerial && { desiredParentSerial: partAction.value?.desiredParentSerial }),
        ...(partAction.value?.desiredParentId && { desiredParentId: partAction.value?.desiredParentId }),
        ...(partAction.value?.desiredParentLocationId && { desiredParentLocationId: partAction.value?.desiredParentLocationId }),
        ...(partAction.value?.locationTypeCode && { locationTypeCode: partAction.value?.locationTypeCode }),
        ...(partAction.value?.deviceImageS3Key && { deviceImageS3Key: partAction.value?.deviceImageS3Key }),
        ...(partAction.value?.deviceImageFileName && { deviceImageFileName: partAction.value?.deviceImageFileName }),
        ...(partAction.value?.serialImageS3Key && { serialImageS3Key: partAction.value?.serialImageS3Key }),
        ...(partAction.value?.serialImageFileName && { serialImageFileName: partAction.value?.serialImageFileName })
      };
    } else if (partAction?.value?.type === PartActionType.REPLACE) {
      inValue = {
        id: partAction.inValue?.id,
        partTypeId: partAction.inValue?.partTypeId,
        name: partAction.inValue?.name,
        sw: partAction.inValue?.sw,
        hw: partAction.inValue?.hw,
        fw: partAction.inValue?.fw,
        ft: partAction.inValue?.ft,
        serial: partAction.inValue?.serial,
        qty: partAction.inValue?.qty,
        category: partAction.inValue?.category,
        condition: partAction.inValue?.condition,
        location: partAction.inValue?.location,
        notes: partAction.outValue?.notes,
        type: partAction.inValue?.type,
        images: [],
        partId: partAction.inValue?.partId,
        ns_partId: partAction.inValue?.ns_partId,
        ...(partAction.inValue?.isExceptionalCreation && { isExceptionalCreation: partAction.inValue?.isExceptionalCreation }),
        ...(partAction.inValue?.deviceImageS3Key && { deviceImageS3Key: partAction.inValue?.deviceImageS3Key }),
        ...(partAction.inValue?.deviceImageFileName && { deviceImageFileName: partAction.inValue?.deviceImageFileName }),
        ...(partAction.inValue?.serialImageS3Key && { serialImageS3Key: partAction.inValue?.serialImageS3Key }),
        ...(partAction.inValue?.serialImageFileName && { serialImageFileName: partAction.inValue?.serialImageFileName })
      };
      outValue = {
        id: partAction.outValue?.id,
        partTypeId: partAction.outValue?.partTypeId,
        name: partAction.outValue?.name,
        sw: partAction.outValue?.sw,
        hw: partAction.outValue?.hw,
        fw: partAction.outValue?.fw,
        ft: partAction.outValue?.ft,
        serial: partAction.outValue?.serial,
        qty: partAction.outValue?.qty,
        category: partAction.outValue?.category,
        condition: partAction.outValue?.condition,
        location: partAction.outValue?.location,
        notes: partAction.outValue?.notes,
        type: partAction.outValue?.type,
        images: [],
        partId: partAction.outValue?.partId,
        ns_partId: partAction.outValue?.ns_partId,
        ...(partAction.outValue?.isExceptionalCreation && { isExceptionalCreation: partAction.outValue?.isExceptionalCreation }),
        ...(partAction.outValue?.deviceImageS3Key && { deviceImageS3Key: partAction.outValue?.deviceImageS3Key }),
        ...(partAction.outValue?.deviceImageFileName && { deviceImageFileName: partAction.outValue?.deviceImageFileName }),
        ...(partAction.outValue?.serialImageS3Key && { serialImageS3Key: partAction.outValue?.serialImageS3Key }),
        ...(partAction.outValue?.serialImageFileName && { serialImageFileName: partAction.outValue?.serialImageFileName })
      };
    } else if (partAction?.value?.type === PartActionType.REMOVE) {
      outValue = {
        id: partAction.outValue?.id,
        partTypeId: partAction.outValue?.partTypeId,
        name: partAction.outValue?.name,
        sw: partAction.outValue?.sw,
        hw: partAction.outValue?.hw,
        fw: partAction.outValue?.fw,
        ft: partAction.outValue?.ft,
        serial: partAction.outValue?.serial,
        qty: partAction.outValue?.qty,
        category: partAction.outValue?.category,
        condition: PartConditionOptions.FAULTY,
        location: partAction.outValue?.location,
        notes: partAction.outValue?.notes,
        type: partAction.outValue?.type,
        images: [],
        partId: partAction.outValue?.partId,
        ns_partId: partAction.outValue?.ns_partId,
        ...(partAction.outValue?.isExceptionalCreation && { isExceptionalCreation: partAction.outValue?.isExceptionalCreation }),
        ...(partAction.outValue?.deviceImageS3Key && { deviceImageS3Key: partAction.outValue?.deviceImageS3Key }),
        ...(partAction.outValue?.deviceImageFileName && { deviceImageFileName: partAction.outValue?.deviceImageFileName }),
        ...(partAction.outValue?.serialImageS3Key && { serialImageS3Key: partAction.outValue?.serialImageS3Key }),
        ...(partAction.outValue?.serialImageFileName && { serialImageFileName: partAction.outValue?.serialImageFileName })
      };
    } else if (partAction?.value?.type === PartActionType.CLEAN) {
      inValue = {
        id: partAction.value?.id,
        partTypeId: partAction.value.partTypeId,
        name: partAction.value?.name,
        sw: partAction.value?.sw,
        hw: partAction.value?.hw,
        fw: partAction.value?.fw,
        ft: partAction.value?.ft,
        serial: partAction.value?.serial,
        qty: partAction.value?.qty,
        category: partAction.value?.category,
        condition: partAction.value?.condition,
        location: partAction.value?.location,
        notes: partAction.value?.notes,
        type: partAction.value?.type,
        images: [],
        partId: partAction.value?.partId,
        ns_partId: partAction.value?.ns_partId
      };
    } else if (partAction?.value?.type === PartActionType.TEST) {
      inValue = {
        id: partAction.value?.id,
        partTypeId: partAction.value.partTypeId,
        name: partAction.value?.name,
        sw: partAction.value?.sw,
        hw: partAction.value?.hw,
        fw: partAction.value?.fw,
        ft: partAction.value?.ft,
        serial: partAction.value?.serial,
        qty: partAction.value?.qty,
        category: partAction.value?.category,
        condition: partAction.value?.condition,
        location: partAction.value?.location,
        notes: partAction.value?.notes,
        type: partAction.value?.type,
        images: [],
        partId: partAction.value?.partId,
        ns_partId: partAction.value?.ns_partId,
        testOutcome: partAction.value?.testOutcome,
        observedFaultId: partAction.value?.observedFaultId,
        observedFaultName: partAction.value?.observedFaultName,
        labourTime: partAction.value?.labourTime,
        ...(partAction.value?.deviceImageS3Key && { deviceImageS3Key: partAction.value?.deviceImageS3Key }),
        ...(partAction.value?.deviceImageFileName && { deviceImageFileName: partAction.value?.deviceImageFileName }),
        ...(partAction.value?.serialImageS3Key && { serialImageS3Key: partAction.value?.serialImageS3Key }),
        ...(partAction.value?.serialImageFileName && { serialImageFileName: partAction.value?.serialImageFileName })
      };
    } else if (partAction?.value?.type === PartActionType.DOA) {
      outValue = {
        id: partAction.outValue?.id,
        partTypeId: partAction.outValue?.partTypeId,
        name: partAction.outValue?.name,
        sw: partAction.outValue?.sw,
        hw: partAction.outValue?.hw,
        fw: partAction.outValue?.fw,
        ft: partAction.outValue?.ft,
        serial: partAction.outValue?.serial,
        qty: partAction.outValue?.qty,
        category: partAction.outValue?.category,
        condition: partAction.outValue?.condition,
        location: partAction.outValue?.location,
        notes: partAction.outValue?.notes,
        type: partAction.outValue?.type,
        images: [],
        partId: partAction.outValue?.partId,
        ns_partId: partAction.outValue?.ns_partId
      };
    } else if (partAction?.value?.type === PartActionType.REPAIR) {
      inValue = {
        id: partAction.value?.id,
        partTypeId: partAction.value.partTypeId,
        name: partAction.value?.name,
        sw: partAction.value?.sw,
        hw: partAction.value?.hw,
        fw: partAction.value?.fw,
        ft: partAction.value?.ft,
        serial: partAction.value?.serial,
        qty: partAction.value?.qty,
        category: partAction.value?.category,
        condition: partAction.value?.condition,
        location: partAction.value?.location,
        notes: partAction.value?.notes,
        type: partAction.value?.type,
        images: [],
        partId: partAction.value?.partId,
        ns_partId: partAction.value?.ns_partId,
        ...(partAction.value?.desiredParentSerial && { desiredParentSerial: partAction.value?.desiredParentSerial }),
        ...(partAction.value?.desiredParentId && { desiredParentId: partAction.value?.desiredParentId }),
        ...(partAction.value?.desiredParentLocationId && { desiredParentLocationId: partAction.value?.desiredParentLocationId }),
        ...(partAction.value?.locationTypeCode && { locationTypeCode: partAction.value?.locationTypeCode }),
        repairActions: partAction.value?.repairActions?.join(','),
        labourTime: partAction.value?.labourTime,
        ...(partAction.value?.deviceImageS3Key && { deviceImageS3Key: partAction.value?.deviceImageS3Key }),
        ...(partAction.value?.deviceImageFileName && { deviceImageFileName: partAction.value?.deviceImageFileName }),
        ...(partAction.value?.serialImageS3Key && { serialImageS3Key: partAction.value?.serialImageS3Key }),
        ...(partAction.value?.serialImageFileName && { serialImageFileName: partAction.value?.serialImageFileName })
      };
    } else if (partAction?.value?.type === PartActionType.ADJUST) {
      inValue = {
        id: partAction.value?.id,
        partTypeId: partAction.value.partTypeId,
        name: partAction.value?.name,
        sw: partAction.value?.sw,
        hw: partAction.value?.hw,
        fw: partAction.value?.fw,
        ft: partAction.value?.ft,
        serial: partAction.value?.serial,
        qty: partAction.value?.qty,
        category: partAction.value?.category,
        condition: partAction.value?.condition,
        location: partAction.value?.location,
        notes: partAction.value?.notes,
        type: partAction.value?.type,
        images: [],
        partId: partAction.value?.partId,
        ns_partId: partAction.value?.ns_partId,
        ...(partAction.value?.desiredParentSerial && { desiredParentSerial: partAction.value?.desiredParentSerial }),
        ...(partAction.value?.desiredParentId && { desiredParentId: partAction.value?.desiredParentId }),
        ...(partAction.value?.desiredParentLocationId && { desiredParentLocationId: partAction.value?.desiredParentLocationId }),
        ...(partAction.value?.locationTypeCode && { locationTypeCode: partAction.value?.locationTypeCode }),
        ...(partAction.value?.deviceImageS3Key && { deviceImageS3Key: partAction.value?.deviceImageS3Key }),
        ...(partAction.value?.deviceImageFileName && { deviceImageFileName: partAction.value?.deviceImageFileName }),
        ...(partAction.value?.serialImageS3Key && { serialImageS3Key: partAction.value?.serialImageS3Key }),
        ...(partAction.value?.serialImageFileName && { serialImageFileName: partAction.value?.serialImageFileName })
      };
    }

    mappedPartactions.push({
      id: partAction.value.id, // For unique part action card identification (for interactions during part actions bulk submit)
      actionId: partAction.value?.actionId, // For saved part action identification (if saved from an action trigger interaction during service creation or etc..)
      execute: true,
      actionTypeCode: partAction.value.type || '',
      serviceId: serviceId,
      installedOrMaintainedPartData: inValue,
      removedPartData: outValue,
      notes: partAction.value.notes || '',
      isExceptionalCreation: partAction.value?.isExceptionalCreation
    });
  }
  return mappedPartactions;
};

export const isPartActionsSubmitDisabled = (partActionCardDataset: CustomisePartActionCard[]) => {
  if (!partActionCardDataset?.length || partActionCardDataset?.every((card) => card.executionCompleted)) return true;
  
  for (const partAction of partActionCardDataset) {
    if ([PartActionType.INSTALL, PartActionType.CLEAN, PartActionType.REPAIR, PartActionType.TEST, PartActionType.ADJUST].includes(partAction?.value?.type as PartActionType)) {
      if (isEmptyString(partAction?.value?.name)) return true;
    } else if ([PartActionType.REMOVE, PartActionType.DOA].includes(partAction?.value?.type as PartActionType)) {
      if (isEmptyString(partAction?.outValue?.name)) return true;
    } else if (partAction?.value?.type === PartActionType.REPLACE) {
      if (isEmptyString(partAction?.inValue?.name) && isEmptyString(partAction?.outValue?.name)) return true;
    }
  }

  return false;
};

export const getExceptionPartImageFileKey = (isDeviceImage: boolean, isOutAction: boolean, cardId: number, partActionCardDataset: CustomisePartActionCard[], imageData?: any) => {
  if (imageData) {
    return imageData.find((image: any) => image.isDeviceImage === isDeviceImage)?.fileKey || imageData.find((image: any) => image.isDeviceImage === isDeviceImage)?.key || '' ;
  } else if (!imageData && partActionCardDataset?.length > 0) {
    if (isDeviceImage) {
      if (isOutAction) {
        return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.outValue?.deviceImageS3Key || '' ;
      } else return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.value?.deviceImageS3Key ||
        partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.inValue?.deviceImageS3Key || '';
    } else {
      if (isOutAction) {
        return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.outValue?.serialImageS3Key || '' ;
      } else return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.value?.serialImageS3Key ||
        partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.inValue?.serialImageS3Key || '';
    }
  } else return '';
};

export const getExceptionPartImageFileName = (isDeviceImage: boolean, isOutAction: boolean, cardId: number, partActionCardDataset: CustomisePartActionCard[], imageData?: any) => {
  if (imageData) {
    return imageData.find((image: any) => image.isDeviceImage === isDeviceImage)?.fileName || '' ;
  } else if (!imageData && partActionCardDataset?.length > 0) {
    if (isDeviceImage) {
      if (isOutAction) {
        return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.outValue?.deviceImageFileName || '' ;
      } else return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.value?.deviceImageFileName ||
        partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.inValue?.deviceImageFileName || '';
    } else {
      if (isOutAction) {
        return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.outValue?.serialImageFileName || '' ;
      } else return partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.value?.serialImageFileName ||
        partActionCardDataset.find((card: CustomisePartActionCard) => card.value?.id === cardId)?.inValue?.serialImageFileName || '';    
    }
  } else return '';
};